import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { COST_SUB_PERMISSIONS, fetchPeritoCostsShared } from "@/lib/perito/peritoCosts";
import { useAuth } from "@/contexts/AuthContext";

export const MANAGEABLE_SECTIONS = [
  { key: "dashboard", route: "/dashboard" },
  { key: "blockMap", route: "/blocks" },
  { key: "planner", route: "/planner" },
  { key: "sprayLogs", route: "/spray-logs" },
  { key: "inventory", route: "/inventory" },
  { key: "analytics", route: "/analytics" },
  { key: "teams", route: "/teams" },
  { key: "pests", route: "/pests" },
  { key: "advancedFeatures", route: "/advanced" },
  // La clave sigue siendo «campaigns» (es lo que hay guardado en la base); solo
  // cambia la dirección, que pasó a colgar de Ajustes en 2026-08-11.
  { key: "campaigns", route: "/settings/campaigns" },
] as const;

export type SectionKey = (typeof MANAGEABLE_SECTIONS)[number]["key"];

/**
 * La MISMA pantalla a la que se llega por dos direcciones. Equipo vive en
 * `/teams` y también colgando de Ajustes, y esa segunda puerta se quedó sin
 * llave: un encargado con Equipo apagado entraba por Ajustes → Equipo y Roles y
 * veía la plantilla entera (usuario 24-09-2026). La correspondencia vive AQUÍ y
 * no en cada pantalla, para que la guardia de la ruta, la tarjeta del menú y
 * cualquier sitio que pregunte contesten lo mismo.
 */
const RUTAS_GEMELAS: Partial<Record<SectionKey, string[]>> = {
  teams: ["/settings/team"],
};

/**
 * Secciones que se REPARTEN como funcionalidad (usuario 24-09-2026).
 *
 * El asistente y Campañas no son sitios del menú diario: el asistente es una
 * herramienta que se enciende o se apaga, y Campañas se configura una vez por
 * temporada desde Ajustes. En la ficha de un miembro van con las Cifras y las
 * Notas de consumo, no con Mapa o Inventario.
 *
 * Es SOLO dónde se pintan: la llave sigue siendo de sección, con su ruta y su
 * guardia (`/advanced`, `/settings/campaigns`). Moverlas de verdad a
 * `SUB_PERMISSIONS` dejaría esas dos rutas sin guardia, que es justo lo que
 * acabamos de arreglar en Equipo.
 */
export const SECCIONES_COMO_FUNCIONALIDAD: SectionKey[] = ["advancedFeatures", "campaigns"];

/** Las secciones que se enseñan como secciones, en su orden. */
export const SECCIONES_DE_MENU = MANAGEABLE_SECTIONS.filter(
  (s) => !SECCIONES_COMO_FUNCIONALIDAD.includes(s.key),
);

/** Sub-permission keys — feature-level toggles, not full section/route blocks */
/* LA FACTURACIÓN NO ES UN PERMISO (usuario 24-09-2026). Estuvo aquí como
   `settingsBilling` y se podía conceder a un encargado; ahí se cambia el plan de
   la empresa y se paga, así que es del admin por ser admin y no se reparte. La
   llave se retira de la lista: el interruptor desaparece de las fichas de
   miembro y la ruta la cierra `SoloAdminGuard`. Las filas que quedan en la base
   con esa llave ya no las lee nadie —ver `DEFAULT_SUB_PERMISSIONS`—, así que no
   hace falta migración. */
/* DATOS DE ACCESO: LA PRIMERA LLAVE QUE NACE APAGADA (usuario 24-09-2026).
   Deja que un responsable cambie el usuario, el correo y la contraseña de los
   TRABAJADORES QUE YA VE —la misma regla de visibilidad que el resto de la
   aplicación—, nunca de un administrador, un perito ni otro responsable.

   Va aquí y no en `MANAGEABLE_SECTIONS` porque no abre una pantalla: afina lo
   que se puede hacer dentro de Equipo, igual que las Cifras afinan Inventario.
   Necesita «Equipo» encendido para servir de algo, porque sin él no se llega a
   la ficha de nadie.

   Y ROMPE LA REGLA DE LA CASA A PROPÓSITO: aquí «sin fila» significa APAGADO,
   al revés que todas las demás. Cambiar la contraseña de alguien es poder
   entrar como esa persona, así que esto no se hereda por omisión — se concede.
   Por eso vive en `DEFAULT_SUB_PERMISSIONS` con `false`: la mezcla parte de ahí
   y el `catch` vuelve ahí, de modo que ni una fila que falta ni un fallo de red
   lo encienden. */
export const SUB_PERMISSIONS = [
  { key: "inventoryNumbers", group: "inventory" },
  { key: "inventoryConsumption", group: "inventory" },
  { key: "teamCredentials", group: "teams" },
] as const;

/** Las que nacen APAGADAS. Ver el comentario de arriba: conceder, no heredar. */
export const SUB_PERMISOS_APAGADOS_POR_DEFECTO: readonly SubPermissionKey[] = ["teamCredentials"];

export type SubPermissionKey = (typeof SUB_PERMISSIONS)[number]["key"];

type PermissionsMap = Record<SectionKey, boolean>;
type SubPermissionsMap = Record<SubPermissionKey, boolean>;

const DEFAULT_PERMISSIONS: PermissionsMap = {
  dashboard: true,
  blockMap: true,
  planner: true,
  sprayLogs: true,
  inventory: true,
  analytics: true,
  teams: true,
  pests: true,
  advancedFeatures: true,
  campaigns: true,
};

const DEFAULT_SUB_PERMISSIONS: SubPermissionsMap = {
  inventoryNumbers: true,
  inventoryConsumption: true,
  /* Apagado. No es un descuido: ver `SUB_PERMISOS_APAGADOS_POR_DEFECTO`. */
  teamCredentials: false,
};

/** Fetch permissions for the current manager user */
export function useManagerPermissions() {
  const { user, company, role } = useAuth();
  const [permissions, setPermissions] = useState<PermissionsMap>(DEFAULT_PERMISSIONS);
  const [subPermissions, setSubPermissions] = useState<SubPermissionsMap>(DEFAULT_SUB_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  /* D4 (Pablo, 26-09-2026): el perito no ve costes salvo que su empresa los
     comparta. Antes este hook le daba «visible» a todo el que no fuera encargado,
     y la ficha del producto le enseñaba valor, coste medio y compras (A6). */
  const [peritoCostsShared, setPeritoCostsShared] = useState(false);

  useEffect(() => {
    if (role !== "perito" || !company?.id) {
      setPeritoCostsShared(false);
      return;
    }
    let cancelled = false;
    fetchPeritoCostsShared(company.id).then((v) => { if (!cancelled) setPeritoCostsShared(v); });
    return () => { cancelled = true; };
  }, [role, company?.id]);

  useEffect(() => {
    if (!user || !company || role !== "manager") {
      setPermissions(DEFAULT_PERMISSIONS);
      setSubPermissions(DEFAULT_SUB_PERMISSIONS);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { data, error } = await supabase
          .from("manager_section_permissions")
          .select("section_key, is_visible")
          .eq("user_id", user.id)
          .eq("company_id", company.id);

        if (error) throw error;

        const map = { ...DEFAULT_PERMISSIONS };
        const subMap = { ...DEFAULT_SUB_PERMISSIONS };
        data?.forEach((row: any) => {
          if (row.section_key in map) {
            (map as any)[row.section_key] = row.is_visible;
          }
          if (row.section_key in subMap) {
            (subMap as any)[row.section_key] = row.is_visible;
          }
        });
        setPermissions(map);
        setSubPermissions(subMap);
      } catch (err) {
        console.error("Error fetching manager permissions:", err);
        setPermissions(DEFAULT_PERMISSIONS);
        setSubPermissions(DEFAULT_SUB_PERMISSIONS);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, company, role]);

  const isSectionVisible = (key: SectionKey) => permissions[key] !== false;

  const isSubPermissionVisible = (key: SubPermissionKey) => {
    if (role === "perito" && (COST_SUB_PERMISSIONS as readonly string[]).includes(key)) return peritoCostsShared;
    if (role !== "manager") return true;
    return subPermissions[key] !== false;
  };

  const isRouteAllowed = (route: string) => {
    if (role !== "manager") return true;
    const section =
      MANAGEABLE_SECTIONS.find((s) => route.startsWith(s.route)) ??
      MANAGEABLE_SECTIONS.find((s) =>
        (RUTAS_GEMELAS[s.key] ?? []).some((r) => route.startsWith(r)),
      );
    if (!section) return true;
    return permissions[section.key] !== false;
  };

  return { permissions, subPermissions, loading, isSectionVisible, isSubPermissionVisible, isRouteAllowed };
}

/** Fetch permissions for a specific user (admin editing) */
export function useManagerPermissionsForUser(userId: string | undefined, companyId: string | undefined) {
  const [permissions, setPermissions] = useState<PermissionsMap>(DEFAULT_PERMISSIONS);
  const [subPermissions, setSubPermissions] = useState<SubPermissionsMap>(DEFAULT_SUB_PERMISSIONS);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    if (!userId || !companyId) {
      setPermissions(DEFAULT_PERMISSIONS);
      setSubPermissions(DEFAULT_SUB_PERMISSIONS);
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from("manager_section_permissions")
        .select("section_key, is_visible")
        .eq("user_id", userId)
        .eq("company_id", companyId);

      if (error) throw error;

      const map = { ...DEFAULT_PERMISSIONS };
      const subMap = { ...DEFAULT_SUB_PERMISSIONS };
      data?.forEach((row: any) => {
        if (row.section_key in map) {
          (map as any)[row.section_key] = row.is_visible;
        }
        if (row.section_key in subMap) {
          (subMap as any)[row.section_key] = row.is_visible;
        }
      });
      setPermissions(map);
      setSubPermissions(subMap);
    } catch (err) {
      console.error("Error fetching manager permissions for user:", err);
      setPermissions(DEFAULT_PERMISSIONS);
      setSubPermissions(DEFAULT_SUB_PERMISSIONS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [userId, companyId]);

  const updatePermission = async (sectionKey: SectionKey | SubPermissionKey, isVisible: boolean) => {
    if (!userId || !companyId) return;

    // Optimistic update
    if (sectionKey in DEFAULT_PERMISSIONS) {
      setPermissions((prev) => ({ ...prev, [sectionKey]: isVisible }));
    }
    if (sectionKey in DEFAULT_SUB_PERMISSIONS) {
      setSubPermissions((prev) => ({ ...prev, [sectionKey]: isVisible }));
    }

    try {
      const { error } = await supabase
        .from("manager_section_permissions")
        .upsert(
          {
            user_id: userId,
            company_id: companyId,
            section_key: sectionKey,
            is_visible: isVisible,
          },
          { onConflict: "user_id,company_id,section_key" }
        );

      if (error) throw error;
    } catch (err) {
      console.error("Error updating manager permission:", err);
      // Revert
      if (sectionKey in DEFAULT_PERMISSIONS) {
        setPermissions((prev) => ({ ...prev, [sectionKey]: !isVisible }));
      }
      if (sectionKey in DEFAULT_SUB_PERMISSIONS) {
        setSubPermissions((prev) => ({ ...prev, [sectionKey]: !isVisible }));
      }
    }
  };

  return { permissions, subPermissions, loading, updatePermission };
}
